import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

// Fetch plates types
export const fetchPlatesTypes = createAsyncThunk(
  "vehicles/fetchPlatesTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/settings/plates-types`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load plates types";
      return rejectWithValue({ message });
    }
  }
);

// Fetch winch partners for filter
export const fetchWinchPartners = createAsyncThunk(
  "vehicles/fetchWinchPartners",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      const { search, page = 1, per_page = 20, ...rest } = params;
      queryParams.set("page", String(page));
      queryParams.set("per_page", String(per_page));
      if (search) queryParams.set("search", String(search));
      Object.entries(rest).forEach(([key, value]) => {
        if (value !== "" && value != null && key !== "search") {
          queryParams.append(key, String(value));
        }
      });
      const url = `${BASE_URL}/partners?${queryParams.toString()}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load winch partners";
      return rejectWithValue({ message });
    }
  }
);

// Fetch drivers for filter
export const fetchDrivers = createAsyncThunk(
  "vehicles/fetchDrivers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      const { search, name, page = 1, per_page = 20, ...rest } = params;
      queryParams.set("page", String(page));
      queryParams.set("per_page", String(per_page));
      const searchTerm = String(search || name || "").trim();
      if (searchTerm) queryParams.set("search", searchTerm);
      Object.entries(rest).forEach(([key, value]) => {
        if (value !== "" && value != null && key !== "search" && key !== "name") {
          queryParams.append(key, String(value));
        }
      });
      const url = `${BASE_URL}/drivers?${queryParams.toString()}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load drivers";
      return rejectWithValue({ message });
    }
  }
);

// Fetch single vehicle details
export const fetchVehicleRecord = createAsyncThunk(
  "vehicles/fetchRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/vehicles/show/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load vehicle details";
      return rejectWithValue(message);
    }
  }
);

// Delete vehicle
export const deleteVehicle = createAsyncThunk(
  "vehicles/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${BASE_URL}/vehicles/delete/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return { id, message: response.data?.message || "Vehicle deleted successfully" };
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete vehicle";
      return rejectWithValue({ message });
    }
  }
);

// all vehicles with filters
export const fetchVehicles = createAsyncThunk(
  "vehicles/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Handle pagination
      if (params.page) {
        queryParams.append("page", params.page);
      }
      if (params.per_page) {
        queryParams.append("per_page", params.per_page);
      }

      // Handle array parameters
      Object.keys(params).forEach((key) => {
        const value = params[key];
        if (key === "page" || key === "per_page") return; // Already handled
        
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => {
            if (item !== "" && item !== null && item !== undefined) {
              queryParams.append(`${key}[]`, item);
            }
          });
        } else if (value !== "" && value !== null && value !== undefined) {
          queryParams.append(key, value);
        }
      });

      // Handle date_value for range
      if (params.date_filter === "range" && Array.isArray(params.date_value) && params.date_value.length === 2) {
        queryParams.append("date_value[0]", params.date_value[0]);
        queryParams.append("date_value[1]", params.date_value[1]);
      } else if (params.date_value && params.date_filter !== "range") {
        queryParams.append("date_value", params.date_value);
      }

      const url = `${BASE_URL}/vehicles${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data; // Return the whole response including pagination
    } catch (error) {
      const message = error.response?.data?.message || "Failed load data";
      return rejectWithValue(message);
    }
  }
);

// Paginated vehicles for react-select dropdowns (plate filter)
export const fetchVehiclesForSelect = createAsyncThunk(
  "vehicles/fetchForSelect",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      const { search, page = 1, per_page = 20 } = params;
      queryParams.set("page", String(page));
      queryParams.set("per_page", String(per_page));
      const searchTerm = String(search || "").trim();
      if (searchTerm) queryParams.set("search", searchTerm);
      const response = await axios.get(`${BASE_URL}/vehicles?${queryParams.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return {
        page,
        search: searchTerm,
        data: response.data,
      };
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load vehicles";
      return rejectWithValue(message);
    }
  }
);

const VehiclesSlice = createSlice({
  name: "vehicles",
  initialState: {
    vehicles: [],
    record: null,
    isLoading: false,
    error: null,
    success: null,
    pagination: null,
    platesTypes: [],
    winchPartners: [],
    winchPartnersPagination: null,
    winchPartnersFilterLoading: false,
    drivers: [],
    driversPagination: null,
    driversFilterLoading: false,
    selectVehicles: [],
    selectVehiclesPagination: null,
    selectVehiclesLoading: false,
    selectVehiclesSearch: "",
    selectVehiclesPendingSearch: "",
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
      state.successDelete = null;
      state.errorDelete = null;
      state.record = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVehicles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVehicles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vehicles = action.payload?.data?.data || [];
        state.pagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchVehicles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      // Fetch plates types
      .addCase(fetchPlatesTypes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPlatesTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.platesTypes = action.payload || [];
      })
      .addCase(fetchPlatesTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to load plates types";
      })
      // Fetch winch partners
      .addCase(fetchWinchPartners.pending, (state) => {
        state.winchPartnersFilterLoading = true;
      })
      .addCase(fetchWinchPartners.fulfilled, (state, action) => {
        state.winchPartnersFilterLoading = false;
        const newData = action.payload?.data || [];
        const page = action.meta.arg?.page || 1;
        if (page === 1) {
          state.winchPartners = newData;
        } else {
          const prev = Array.isArray(state.winchPartners) ? state.winchPartners : [];
          const ids = new Set(prev.map((r) => r.id));
          state.winchPartners = [
            ...prev,
            ...newData.filter((r) => r?.id != null && !ids.has(r.id)),
          ];
        }
        state.winchPartnersPagination = action.payload?.pagination || null;
      })
      .addCase(fetchWinchPartners.rejected, (state, action) => {
        state.winchPartnersFilterLoading = false;
        state.error = action.payload?.message || "Failed to load winch partners";
      })
      // Fetch drivers
      .addCase(fetchDrivers.pending, (state) => {
        state.driversFilterLoading = true;
      })
      .addCase(fetchDrivers.fulfilled, (state, action) => {
        state.driversFilterLoading = false;
        const newData = action.payload?.data || [];
        const page = action.meta.arg?.page || 1;
        const append = Boolean(action.meta.arg?.append);
        if (append && page > 1) {
          const prev = Array.isArray(state.drivers) ? state.drivers : [];
          const ids = new Set(prev.map((r) => r.id));
          state.drivers = [
            ...prev,
            ...newData.filter((r) => r?.id != null && !ids.has(r.id)),
          ];
        } else {
          state.drivers = newData;
        }
        state.driversPagination = action.payload?.pagination || null;
      })
      .addCase(fetchDrivers.rejected, (state, action) => {
        state.driversFilterLoading = false;
        state.error = action.payload?.message || "Failed to load drivers";
      })
      .addCase(fetchVehiclesForSelect.pending, (state, action) => {
        state.selectVehiclesLoading = true;
        state.selectVehiclesPendingSearch = String(action.meta.arg?.search || "").trim();
      })
      .addCase(fetchVehiclesForSelect.fulfilled, (state, action) => {
        state.selectVehiclesLoading = false;
        const { page, search = "", data } = action.payload || {};
        const pendingSearch = state.selectVehiclesPendingSearch;
        if (page === 1 && search !== pendingSearch) {
          return;
        }
        if (page > 1 && search !== state.selectVehiclesSearch) {
          return;
        }
        const newData = data?.data?.data || [];
        const pagination = data?.data?.pagination || null;
        if (page === 1) {
          state.selectVehicles = newData;
        } else {
          const prev = Array.isArray(state.selectVehicles) ? state.selectVehicles : [];
          const ids = new Set(prev.map((r) => r.id));
          state.selectVehicles = [
            ...prev,
            ...newData.filter((r) => r?.id != null && !ids.has(r.id)),
          ];
        }
        state.selectVehiclesPagination = pagination;
        state.selectVehiclesSearch = search;
        state.selectVehiclesPendingSearch = search;
      })
      .addCase(fetchVehiclesForSelect.rejected, (state) => {
        state.selectVehiclesLoading = false;
      })
      // Fetch vehicle record
      .addCase(fetchVehicleRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.record = null;
      })
      .addCase(fetchVehicleRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.record = action.payload;
      })
      .addCase(fetchVehicleRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load vehicle details";
        state.record = null;
      })
      // Delete vehicle
      .addCase(deleteVehicle.pending, (state) => {
        state.isLoading = true;
        state.errorDelete = null;
        state.successDelete = null;
      })
      .addCase(deleteVehicle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.successDelete = action.payload.message;
        state.vehicles = state.vehicles.filter((v) => v.id !== action.payload.id);
        state.record = null;
      })
      .addCase(deleteVehicle.rejected, (state, action) => {
        state.isLoading = false;
        state.errorDelete = action.payload?.message || "Failed to delete vehicle";
      });
  },
});

export const { clearState } = VehiclesSlice.actions;
export default VehiclesSlice.reducer;
